import { LineBuffer, Particles, makeOrb, removeObj, tint, clamp } from "../shared";

const TR = 160, COLORS = [0xffb800, 0x00f0ff, 0xf43f5e, 0x10b981, 0x8b5cf6, 0xffffff];

class Gravity {
  constructor(ctx) {
    this.ctx = ctx; this.writesColors = true; this.bodies = [];
    this.trails = new LineBuffer(ctx.overlay, 40 * TR, 0.9);
    this.aimLine = new LineBuffer(ctx.overlay, 2, 1);
    this.dust = new Particles(ctx.overlay, 900, 0x9fdcff, 0.045);
    this.preset("solar");
  }
  get G() { return this.ctx.params.G * this.ctx.g.force; }
  spawn(x, z, vx, vz, m, color) {
    const c = color ?? COLORS[this.bodies.length % COLORS.length], b = { x, z, vx, vz, m, color: c, trail: [], mesh: makeOrb(this.ctx.overlay, c, 0.1 + 0.09 * Math.cbrt(m)) };
    this.bodies.push(b); return b;
  }
  clearBodies() { this.bodies.forEach((b) => removeObj(this.ctx.overlay, b.mesh)); this.bodies = []; }
  circular(x, z, m, dir = 1) {
    let M = 0, cx = 0, cz = 0; for (const b of this.bodies) { M += b.m; cx += b.x * b.m; cz += b.z * b.m; }
    if (!M) return this.spawn(x, z, 0, 0, m);
    cx /= M; cz /= M; const dx = x - cx, dz = z - cz, r = Math.hypot(dx, dz) || 1, v = Math.sqrt((this.G * M) / r) * this.ctx.params.vfac;
    return this.spawn(x, z, (-dz / r) * v * dir, (dx / r) * v * dir, m);
  }
  preset(k) {
    this.clearBodies(); const G = this.G;
    if (k === "solar") { this.spawn(0, 0, 0, 0, 20, 0xffb800); [1.3, 2.1, 3.0, 4.1].forEach((r, i) => { const v = Math.sqrt((G * 20) / r); this.spawn(r, 0, 0, v * (i % 2 ? 1 : 1), 0.08 + i * 0.12); }); }
    if (k === "binary") { const v = Math.sqrt((G * 8) / 4.8); this.spawn(-1.2, 0, 0, -v, 8, 0x00f0ff); this.spawn(1.2, 0, 0, v, 8, 0xf43f5e); const vp = Math.sqrt((G * 16) / 4); this.spawn(0, 4, vp, 0, 0.1); }
    if (k === "cluster") for (let i = 0; i < 14; i++) { const a = Math.random() * 6.28, r = 0.8 + Math.random() * 3.4; this.spawn(Math.cos(a) * r, Math.sin(a) * r, -Math.sin(a) * 0.6, Math.cos(a) * 0.6, 0.5 + Math.random() * 1.5); }
    if (k === "figure8") {
      const s = 2, m = 3, kv = Math.sqrt((G * m) / s), vx = 0.93240737 * kv, vz = 0.86473146 * kv;
      this.spawn(0.97000436 * s, -0.24308753 * s, vx / 2, vz / 2, m, 0x00f0ff); this.spawn(-0.97000436 * s, 0.24308753 * s, vx / 2, vz / 2, m, 0xf43f5e); this.spawn(0, 0, -vx, -vz, m, 0xffb800);
    }
    for (let i = 0; i < this.dust.count; i++) this.respawnDust(i);
  }
  respawnDust(i) {
    let M = 0; for (const b of this.bodies) M += b.m;
    const a = Math.random() * 6.28, r = 1 + Math.random() * 3.8, v = M ? Math.sqrt((this.G * M) / r) : 0, d = this.dust;
    d.pos[i * 3] = Math.cos(a) * r; d.pos[i * 3 + 2] = Math.sin(a) * r; d.vel[i * 2] = -Math.sin(a) * v; d.vel[i * 2 + 1] = Math.cos(a) * v;
  }
  accel(x, z, skip) {
    let ax = 0, az = 0;
    for (const b of this.bodies) { if (b === skip) continue; const dx = b.x - x, dz = b.z - z, r2 = dx * dx + dz * dz + 0.0144, f = (this.G * b.m) / (r2 * Math.sqrt(r2)); ax += dx * f; az += dz * f; }
    return [ax, az];
  }
  pointer(e) {
    if (e.type === "down") this.aim = { x: e.x, z: e.z, ex: e.x, ez: e.z };
    else if (e.type === "move" && this.aim) { this.aim.ex = e.x; this.aim.ez = e.z; }
    else if (e.type === "up" && this.aim) {
      const a = this.aim, dx = a.x - a.ex, dz = a.z - a.ez; this.aim = null;
      if (this.bodies.length >= 30) return;
      if (Math.hypot(dx, dz) < 0.25) this.circular(a.x, a.z, this.ctx.params.mass);
      else this.spawn(a.x, a.z, dx * 1.4, dz * 1.4, this.ctx.params.mass);
    }
  }
  integrate(dt) {
    const B = this.bodies, sub = 6, h = dt / sub;
    for (let s = 0; s < sub; s++) {
      for (const b of B) { const [ax, az] = this.accel(b.x, b.z, b); b.vx += ax * h; b.vz += az * h; }
      for (const b of B) { b.x += b.vx * h; b.z += b.vz * h; }
    }
    if (this.ctx.params.merge) for (let i = 0; i < B.length; i++) for (let j = i + 1; j < B.length; j++) {
      const a = B[i], b = B[j], rr = 0.1 + 0.09 * Math.cbrt(a.m) + 0.1 + 0.09 * Math.cbrt(b.m);
      if (Math.hypot(a.x - b.x, a.z - b.z) < rr * 0.7) {
        const m = a.m + b.m; a.vx = (a.vx * a.m + b.vx * b.m) / m; a.vz = (a.vz * a.m + b.vz * b.m) / m;
        a.x = (a.x * a.m + b.x * b.m) / m; a.z = (a.z * a.m + b.z * b.m) / m; a.m = m; a.mesh.scale.setScalar(0.1 + 0.09 * Math.cbrt(m));
        removeObj(this.ctx.overlay, b.mesh); B.splice(j, 1); j--;
      }
    }
    for (let i = B.length - 1; i >= 0; i--) if (Math.abs(B[i].x) > 9 || Math.abs(B[i].z) > 9) { removeObj(this.ctx.overlay, B[i].mesh); B.splice(i, 1); }
  }
  step(dt) {
    const P = this.ctx.params, { N, px, target, colors } = this.ctx, G = this.G;
    this.integrate(dt);
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      let pot = 0; for (const b of this.bodies) { const dx = px[c] - b.x, dz = px[r] - b.z; pot += b.m / Math.sqrt(dx * dx + dz * dz + 0.16); }
      const i = r * N + c, h = clamp(0.7 - P.well * G * pot * 0.06, -1, 1); target[i] = h; tint(colors, i, -(0.7 - h) * 0.7, 0.85);
    }
    const T = this.trails; T.begin();
    for (const b of this.bodies) {
      const rad = 0.1 + 0.09 * Math.cbrt(b.m), y = this.ctx.sampleY(b.x, b.z) + rad; b.mesh.position.set(b.x, y, b.z);
      b.trail.push(b.x, y, b.z); if (b.trail.length > TR * 3) b.trail.splice(0, 3);
      if (!P.trails) continue;
      const col = b.mesh.material.color, tr = b.trail;
      for (let k = 3; k < tr.length; k += 3) { const f = k / tr.length; T.seg(tr[k - 3], tr[k - 2], tr[k - 1], tr[k], tr[k + 1], tr[k + 2], col.r * f, col.g * f, col.b * f); }
    }
    T.end();
    const A = this.aimLine; A.begin();
    if (this.aim) { const y = this.ctx.top + 0.3, a = this.aim; A.seg(a.x, y, a.z, a.x + (a.x - a.ex), y, a.z + (a.z - a.ez), 1, 0.72, 0.1, 1, 1, 1); }
    A.end();
    const d = this.dust; d.visible = P.dust && this.bodies.length > 0;
    if (d.obj.visible) for (let i = 0; i < d.count; i++) {
      const x = d.pos[i * 3], z = d.pos[i * 3 + 2], [ax, az] = this.accel(x, z);
      d.vel[i * 2] += ax * dt; d.vel[i * 2 + 1] += az * dt;
      const nx = x + d.vel[i * 2] * dt, nz = z + d.vel[i * 2 + 1] * dt;
      d.pos[i * 3] = nx; d.pos[i * 3 + 2] = nz; d.pos[i * 3 + 1] = this.ctx.sampleY(nx, nz) + 0.06;
      if (Math.abs(nx) > 6 || Math.abs(nz) > 6 || Math.hypot(ax, az) > 60) this.respawnDust(i);
    }
    d.update();
  }
  action(id) { this.ctx.pushHistory(); this.preset(id); }
  snapshot() { return this.bodies.map(({ x, z, vx, vz, m, color }) => ({ x, z, vx, vz, m, color })); }
  restore(s) { this.clearBodies(); s.forEach((b) => this.spawn(b.x, b.z, b.vx, b.vz, b.m, b.color)); }
  clear() { this.clearBodies(); }
  telemetry() {
    let ke = 0, pe = 0, px = 0, pz = 0; const B = this.bodies;
    for (let i = 0; i < B.length; i++) { const b = B[i]; ke += 0.5 * b.m * (b.vx ** 2 + b.vz ** 2); px += b.m * b.vx; pz += b.m * b.vz; for (let j = i + 1; j < B.length; j++) pe -= (this.G * b.m * B[j].m) / Math.hypot(b.x - B[j].x, b.z - B[j].z, 0.12); }
    return [["Bodies", B.length], ["Kinetic", ke.toFixed(2)], ["Potential", pe.toFixed(2)], ["Total E", (ke + pe).toFixed(2)], ["|p|", Math.hypot(px, pz).toFixed(2)]];
  }
  dispose() { this.clearBodies(); this.trails.dispose(); this.aimLine.dispose(); this.dust.dispose(); }
}

const gravity = {
  id: "gravity-lab", name: "Gravity Lab", icon: "Orbit", rest: 0.7,
  desc: "N-body gravity on a space-time sheet. Tap to drop a body into a circular orbit, or drag to slingshot it (pull back like a catapult). The lattice sinks into each gravitational well; colliding bodies merge, conserving momentum.",
  math: "a = G Σ mⱼ r̂ / (r² + ε²)   ·   Φ = −G Σ m / r",
  params: [
    { key: "G", label: "Gravitational constant G", type: "range", min: 0.1, max: 3, step: 0.05, def: 1 },
    { key: "mass", label: "Spawn mass", type: "range", min: 0.05, max: 6, step: 0.05, def: 0.5 },
    { key: "vfac", label: "Orbit velocity factor", type: "range", min: 0, max: 1.6, step: 0.01, def: 1 },
    { key: "well", label: "Curvature depth", type: "range", min: 0.2, max: 2.5, step: 0.05, def: 1 },
    { key: "trails", label: "Orbital trails", type: "toggle", def: true },
    { key: "dust", label: "Test-particle dust", type: "toggle", def: true },
    { key: "merge", label: "Merge on collision", type: "toggle", def: true },
  ],
  actions: [{ id: "solar", label: "Star system" }, { id: "binary", label: "Binary stars" }, { id: "figure8", label: "Figure-8 three-body" }, { id: "cluster", label: "Random cluster" }],
  create: (ctx) => new Gravity(ctx),
};
export default gravity;
